import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Typography, LinearProgress, Box } from '@mui/joy';
import { format } from 'date-fns';
import createApi, { deleteExpense } from '../api';
import SwipeableExpenseCard from './SwipeableExpenseCard';

const DailyExpenses = forwardRef(({ initDataRaw, onExpenseDeleted, onEditExpense }, ref) => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const api = createApi(initDataRaw);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/expenses/today');
            const formattedData = response.data.map(item => ({
                ...item,
                date: new Date(item.date)
            }));
            setExpenses(formattedData);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
            console.error('Fetch error:', err);
        }
    }, [initDataRaw]);

    useImperativeHandle(ref, () => ({ refresh: fetchData }));

    useEffect(() => {
        if (initDataRaw) {
            fetchData();
        } else {
            setError('Authorization data missing');
            setLoading(false);
        }
    }, [initDataRaw, fetchData]);

    const handleDelete = async (expense) => {
        setDeleting(expense.id);
        try {
            await deleteExpense(api, expense.id);
            setExpenses(expenses.filter(e => e.id !== expense.id));
            onExpenseDeleted?.();
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
        } catch (err) {
            console.error('Delete error:', err);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
        } finally {
            setDeleting(null);
        }
    };

    if (loading) return <LinearProgress />;
    if (error) return <Typography color="danger">Error: {error}</Typography>;

    return (
        <Box sx={{ mb: 3 }}>
            <Typography level="title-md" sx={{ mb: 1 }}>
                Траты за сегодня ({format(new Date(), 'dd.MM.yyyy')})
            </Typography>
            {expenses.length > 0 ? (
                expenses.map((expense) => (
                    <SwipeableExpenseCard
                        key={expense.id}
                        expense={expense}
                        dateFormat="HH:mm"
                        onEdit={onEditExpense}
                        onDelete={handleDelete}
                        deleting={deleting === expense.id}
                    />
                ))
            ) : (
                <Typography level="body-sm" color="neutral" sx={{ py: 2, textAlign: 'center' }}>
                    Нет трат за сегодня. Нажмите +, чтобы добавить.
                </Typography>
            )}
        </Box>
    );
});

export default DailyExpenses;
